import React, { useEffect } from "react";
import {
  Box,
  TextField,
  Grid,
  Autocomplete,
  useAutocomplete,
  Button,
  RefreshButton,
  Edit,
} from "@pankod/refine-mui";
import { Controller, useForm } from "@pankod/refine-react-hook-form";
import { IEmployee, IFinishedPieces } from "interfaces";

import { useModal, useNavigation, useTranslate } from "@pankod/refine-core";
import { useParams } from "react-router-dom";
import { FinishedPiecesModal } from "components/Modals/finished_pieces";
import { AddOutlined } from "@mui/icons-material";
import { finishedPiecesColumns } from "./grid_columns";

export const DetailedEdit: React.FC = () => {
  const params = useParams();
  const { id } = params as any;

  const [totalPiece, setTotalPiece] = React.useState(0);
  const [finishedPiece, setFinishedPiece] = React.useState<number>(0);
  const [realWeight, setRealWeight] = React.useState<number>();

  const [finishedPieces, setFinishedPieces] = React.useState<IFinishedPieces[]>(
    []
  );

  const [prg, setPrg] = React.useState();
  const [decrease, setDecrease] = React.useState(0);

  const { list } = useNavigation();
  const translate = useTranslate();

  const {
    refineCore: { formLoading, onFinish, queryResult },
    saveButtonProps,
    control,
    getValues,
    formState: { errors },
    handleSubmit,
    // getValues,
  } = useForm({
    refineCoreProps: {
      resource: "details/edit",
      redirect: false,
      id: id,
      metaData: {
        populate: ["employee", "order.engrave"],
      },
      onMutationSuccess: () => {
        list("orders/open");
      },
      successNotification: () => {
        return {
          description: translate("notifications.success"),
          message: translate("details.edit.success"),
          type: "success",
        };
      },
    },
  });

  const data = queryResult?.data?.data;

  const onSubmit = (newData: any) => {
    onFinish({
      ...newData,
      order: data?.order?.id,
      realWeight: realWeight,
      finishedPiece: finishedPiece,
      decrease: decrease,
      finishedPieces: finishedPieces,
    });
  };

  const { autocompleteProps } = useAutocomplete<IEmployee>({
    resource: "employees",
    filters: [{ field: "active", operator: "eq", value: true }],
    onSearch: (search) => [
      {
        field: "name",
        operator: "contains",
        value: search,
      },
    ],
  });

  const {
    show: showFinishedPiecesModal,
    close: closeFinishedPiecesModal,
    visible: visibleFinishedPiecesModal,
  } = useModal();

  const handleAddFinishedPieces = (newData: any) => {
    const newWeight = newData?.total_real_weight;
    const newFinishedPiece = newData?.total_qty;

    setRealWeight(newWeight);
    setFinishedPiece(newFinishedPiece);

    setFinishedPieces([
      ...finishedPieces,
      {
        ...newData,
        process: "Detallado",
        order: data?.order?.id,
      },
    ]);
  };

  const getDecrease = () => {
    const prpData = prg ? prg : 0;
    const realWeightData = realWeight ? realWeight : 0;

    const decrease = prpData - realWeightData;
    setDecrease(+decrease.toFixed(2));
  };

  useEffect(() => {
    if (data) {
      setTotalPiece(data?.order?.total_pieces);
      setFinishedPiece(data?.finished_pieces);
      setRealWeight(data?.real_weight || 0);
      setPrg(data?.order?.engrave?.real_weight || 0);
    }
  }, [data]);

  useEffect(() => {
    if (realWeight && prg) {
      getDecrease();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [realWeight, prg]);

  return (
    <Edit
      isLoading={formLoading}
      saveButtonProps={{
        ...saveButtonProps,
        disabled: formLoading,
        onClick: handleSubmit(onSubmit),
      }}
      resource="details"
      headerButtons={() => {
        return (
          <RefreshButton
            resourceNameOrRouteName="details"
            recordItemId={id}
            metaData={{ populate: ["employee"] }}
          />
        );
      }}
    >
      <Box
        component="form"
        sx={{ display: "flex", flexDirection: "column" }}
        autoComplete="off"
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Controller
              control={control}
              name="employee"
              rules={{ required: "El campo empleado es obligatorio." }}
              defaultValue={null as any}
              render={({ field }) => {
                return (
                  <Autocomplete
                    {...autocompleteProps}
                    {...field}
                    onChange={(_, value) => {
                      field.onChange(value);
                    }}
                    getOptionLabel={(item) => {
                      return (
                        autocompleteProps?.options?.find(
                          (p) => p?.id?.toString() === item?.id?.toString()
                        )?.name ?? ""
                      );
                    }}
                    isOptionEqualToValue={(option, value) => {
                      return (
                        value === undefined ||
                        option.id.toString() === value.id.toString()
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Empleado"
                        margin="normal"
                        variant="outlined"
                        error={!!errors.employee}
                        helperText={errors.employee?.message?.toString()}
                        required
                      />
                    )}
                  />
                );
              }}
            />
          </Grid>
          <Grid item xs={12} />
          <Grid item xs={12} sm={6}>
            <Grid container spacing={2}>
              <Grid item xs={3}>
                <TextField
                  margin="normal"
                  fullWidth
                  id="finished_pieces"
                  label="Pz. Totales"
                  name="finished_pieces"
                  value={totalPiece || 0}
                  type="number"
                  className="readonly"
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  margin="normal"
                  fullWidth
                  id="finished_pieces"
                  label="Pz. Terminadas"
                  name="finished_pieces"
                  value={finishedPiece || 0}
                  type="number"
                  className="readonly"
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  margin="normal"
                  fullWidth
                  id="real_weight"
                  label="PRD"
                  name="real_weight"
                  value={realWeight || 0}
                  className="readonly"
                />
              </Grid>
              <Grid
                item
                xs={3}
                alignItems={"center"}
                justifyContent={"center"}
                display={"flex"}
              >
                <Button
                  color={"info"}
                  size={"small"}
                  onClick={() => showFinishedPiecesModal()}
                >
                  <AddOutlined /> Agregar Piezas
                </Button>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              margin="normal"
              fullWidth
              id="prg"
              label="PRG"
              name="prg"
              value={prg || 0}
              className="readonly"
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              margin="normal"
              fullWidth
              id="decrease"
              label="Merma"
              name="decrease"
              value={decrease || 0}
              className="readonly"
            />
          </Grid>
          <FinishedPiecesModal
            visible={visibleFinishedPiecesModal}
            close={closeFinishedPiecesModal}
            title={"Piezas terminadas"}
            data={{
              currentEmployee: getValues("employee"),
              orderQty: totalPiece || 0,
              currentQty: finishedPiece || 0,
              realWeight: realWeight,
            }}
            setData={handleAddFinishedPieces}
            history={{
              config: {
                filters: [
                  {
                    field: "process",
                    operator: "eq",
                    value: "Detallado",
                  },
                  {
                    field: "order",
                    operator: "" as any,
                    value: data?.order?.id,
                  },
                ],
              },
              metaData: {
                populate: ["employee"],
              },
              columns: finishedPiecesColumns,
              extraData: finishedPieces,
            }}
          />
        </Grid>
      </Box>
    </Edit>
  );
};
